import { ApiError } from './errors'
import { newRequestId } from './requestId'
import { getWorkTenantId, WORK_TENANT_HEADER } from './workTenant'

/**
 * ADR-0024 D9.1 - nenhum componente de tela chama `fetch` diretamente.
 * ADR-0024 D9.2 - credenciais do cookie (`credentials: 'include'`) e header
 *                 CSRF em toda chamada que altera estado.
 * ADR-0024 D5.1 - protecao CSRF por cookie de double submit: o backend grava
 *                 `XSRF-TOKEN` (legivel pelo JS) e a SPA o ecoa em
 *                 `X-XSRF-TOKEN`. Enquanto o bloco B1 nao entrega o cookie, a
 *                 funcao simplesmente nao encontra o valor e omite o header.
 */

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? ''
const CSRF_COOKIE = 'XSRF-TOKEN'
const CSRF_HEADER = 'X-XSRF-TOKEN'
const REQUEST_ID_HEADER = 'X-Request-ID'
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null
}

export interface ApiRequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
  requestId?: string
  /** s62 / PDF: devolve o corpo como arquivo (Blob), nao como JSON. */
  blob?: boolean
}

export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase()
  const requestId = options.requestId ?? newRequestId()

  const headers: Record<string, string> = {
    Accept: options.blob ? 'application/pdf, application/json' : 'application/json',
    [REQUEST_ID_HEADER]: requestId,
  }

  // s54: empresa em que o administrador esta trabalhando (so existe para quem tem tenant.list).
  const workTenant = getWorkTenantId()
  if (workTenant) headers[WORK_TENANT_HEADER] = workTenant

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  if (MUTATING_METHODS.has(method)) {
    const csrfToken = readCookie(CSRF_COOKIE)
    if (csrfToken) headers[CSRF_HEADER] = csrfToken
  }

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      ...(options.signal ? { signal: options.signal } : {}),
    })
  } catch (cause) {
    throw new ApiError(
      cause instanceof Error ? cause.message : 'network failure',
      0,
      requestId,
      cause,
    )
  }

  const responseRequestId = response.headers.get(REQUEST_ID_HEADER) ?? requestId

  if (!response.ok) {
    let detail: unknown = null
    try {
      detail = await response.json()
    } catch {
      detail = await response.text().catch(() => null)
    }
    throw new ApiError(`HTTP ${response.status}`, response.status, responseRequestId, detail)
  }

  if (response.status === 204) return undefined as T
  if (options.blob) return (await response.blob()) as T
  return (await response.json()) as T
}

export const __testables = { BASE_URL, CSRF_COOKIE, CSRF_HEADER, REQUEST_ID_HEADER }
